// EN/FR copy for the Team Dashboard summary, members and indicators. These sections render their
// own language (data-cvi-i18n="managed").

const plural = (n, one, many) => (n === 1 ? one : many);

const COPY = {
    en: {
        locale: "en-CA",
        teamFallback: "Team",
        state: { final: "Final", provisional: "Provisional", unavailable: "Not available" },
        summary: {
            label: "Team summary",
            teamCvi: "Team CVI",
            outOf: "/ 100",
            coverage: (n, total) => `${n} of ${total} ${plural(total, "member", "members")} with a CVI`,
            mean: "Average of the members who have a result",
            noResult: "No member has a CVI for this period yet.",
            members: "Team members",
            assessed: n => `${n} with a result`,
            attainment: "Indicator achievement",
            attainmentHelp: (withData, total) => total ? `${withData} of ${total} ${plural(total, "indicator", "indicators")} with approved data` : "No team indicators",
            attainmentNone: "No indicator result for this period",
            reviews: "Approvals waiting",
            reviewsHelp: "Contributions to approve",
            reviewsOpen: "Open Approvals",
            support: "Open support needs",
            supportHelp: "Open or in progress",
            currentOnly: "Approvals and support needs are shown for the current period only.",
            historical: "Finalized period · read-only. Results come from the stored final snapshots.",
            loading: "Loading team results…",
            error: "Team results could not be loaded.",
        },
        attention: {
            label: "Needs attention",
            notAssessed: n => `${n} ${plural(n, "member", "members")} without a result`,
            reviews: n => `${n} ${plural(n, "approval", "approvals")} waiting`,
            support: n => `${n} open support ${plural(n, "need", "needs")}`,
            below: n => `${n} ${plural(n, "indicator", "indicators")} below target`,
            noData: n => `${n} ${plural(n, "indicator", "indicators")} without approved data`,
            pending: n => `${n} indicator ${plural(n, "value", "values")} awaiting approval`,
            none: "Nothing needs your attention right now.",
        },
        members: {
            title: "Team Members",
            subtitle: "Results for the selected period, highest CVI first.",
            member: "Member", cvi: "CVI", individual: "Individual", collective: "Collective",
            individualFull: "Individual Contribution", collectiveFull: "Collective Contribution",
            reviews: "Approvals", status: "Status",
            waiting: n => `${n} waiting`, none: "None",
            noResult: "No result yet",
            empty: "No team members are currently assigned.",
        },
        indicators: {
            title: "Team Indicators",
            subtitle: "Achievement calculated by the CVI engine from approved values.",
            counts: { on_track: "On track", watch: "Watch", critical: "Below target", no_data: "No approved data", no_result: "Result not available" },
            actual: "Actual", target: "Target", weight: "Weight", weightHelp: "Relative weight of this indicator in the team’s indicator score",
            approvedValues: n => `${n} approved ${plural(n, "value", "values")}`,
            awaiting: n => `${n} awaiting approval`,
            revision: n => `${n} returned for revision`,
            notSubmitted: "No value submitted",
            empty: "No team indicators are configured for this period.",
        },
    },
    fr: {
        locale: "fr-CA",
        teamFallback: "Équipe",
        state: { final: "Final", provisional: "Provisoire", unavailable: "Non disponible" },
        summary: {
            label: "Sommaire de l’équipe",
            teamCvi: "CVI de l’équipe",
            outOf: "/ 100",
            coverage: (n, total) => `${n} ${plural(total, "membre", "membres")} sur ${total} avec un CVI`,
            mean: "Moyenne des membres qui ont un résultat",
            noResult: "Aucun membre n’a encore de CVI pour cette période.",
            members: "Membres de l’équipe",
            assessed: n => `${n} avec un résultat`,
            attainment: "Atteinte des indicateurs",
            attainmentHelp: (withData, total) => total ? `${withData} ${plural(withData <= 1 ? 1 : withData, "indicateur", "indicateurs")} sur ${total} avec des données approuvées` : "Aucun indicateur d’équipe",
            attainmentNone: "Aucun résultat d’indicateur pour cette période",
            reviews: "Approbations en attente",
            reviewsHelp: "Contributions à approuver",
            reviewsOpen: "Ouvrir Approbations",
            support: "Besoins de soutien ouverts",
            supportHelp: "Ouverts ou en cours",
            currentOnly: "Les approbations et les besoins de soutien sont affichés pour la période en cours seulement.",
            historical: "Période finalisée · lecture seule. Les résultats proviennent des instantanés finaux enregistrés.",
            loading: "Chargement des résultats de l’équipe…",
            error: "Les résultats de l’équipe n’ont pas pu être chargés.",
        },
        attention: {
            label: "À surveiller",
            notAssessed: n => `${n} ${plural(n, "membre", "membres")} sans résultat`,
            reviews: n => `${n} ${plural(n, "approbation", "approbations")} en attente`,
            support: n => `${n} ${plural(n, "besoin de soutien ouvert", "besoins de soutien ouverts")}`,
            below: n => `${n} ${plural(n, "indicateur", "indicateurs")} sous la cible`,
            noData: n => `${n} ${plural(n, "indicateur", "indicateurs")} sans données approuvées`,
            pending: n => `${n} ${plural(n, "valeur d’indicateur", "valeurs d’indicateurs")} en attente d’approbation`,
            none: "Rien ne demande votre attention pour le moment.",
        },
        members: {
            title: "Membres de l’équipe",
            subtitle: "Résultats de la période sélectionnée, du CVI le plus élevé au plus bas.",
            member: "Membre", cvi: "CVI", individual: "Individuelle", collective: "Collective",
            individualFull: "Contribution individuelle", collectiveFull: "Contribution collective",
            reviews: "Approbations", status: "Statut",
            waiting: n => `${n} en attente`, none: "Aucune",
            noResult: "Aucun résultat",
            empty: "Aucun membre n’est actuellement assigné à l’équipe.",
        },
        indicators: {
            title: "Indicateurs de l’équipe",
            subtitle: "Atteinte calculée par le moteur CVI à partir des valeurs approuvées.",
            counts: { on_track: "Conforme", watch: "À surveiller", critical: "Sous la cible", no_data: "Aucune donnée approuvée", no_result: "Résultat non disponible" },
            actual: "Réel", target: "Cible", weight: "Pondération", weightHelp: "Poids relatif de cet indicateur dans le résultat des indicateurs de l’équipe",
            approvedValues: n => `${n} ${plural(n, "valeur approuvée", "valeurs approuvées")}`,
            awaiting: n => `${n} en attente d’approbation`,
            revision: n => `${n} ${plural(n, "renvoyée", "renvoyées")} pour révision`,
            notSubmitted: "Aucune valeur soumise",
            empty: "Aucun indicateur d’équipe n’est configuré pour cette période.",
        },
    },
};

export function teamCopy(language) {
    return COPY[language === "fr" ? "fr" : "en"];
}
